<template>
    <div class="container admin-table-page">
        <!-- HEADER -->
        <div class="param-title-wrap niif-header">
            <div class="d-flex align-items-center w-100">
                <div class="d-flex align-items-center">
                    <b-button class="page-back-btn mr-2" variant="link" @click="goBack">
                        <font-awesome-icon :icon="['fas', 'arrow-alt-circle-left']" class="back-icon" />
                    </b-button>
                    <h2 class="param-title">{{ labels.title }}</h2>
                </div>
            </div>
        </div>

        <!-- FILTROS -->
        <div class="param-title-wrap niif-header">
            <div class="d-flex align-items-center w-100">
                <div class="filters-container d-flex align-items-center">
                    <b-form-select v-model="selVigencia" @change="onFilterChange" class="filter-select ml-2">
                        <b-form-select-option value="">{{ labels.selectValidity }}</b-form-select-option>
                        <option v-for="v in listVigencia" :key="v" :value="v">{{ v }}</option>
                    </b-form-select>

                    <b-form-select v-model="selFormulario" @change="onFormularioChange" class="filter-select">
                        <b-form-select-option value="">{{ labels.selectForm }}</b-form-select-option>
                        <option v-for="f in listFormulario" :key="f.formulario" :value="f.formulario">
                            {{ f.descripcion }}
                        </option>
                    </b-form-select>

                    <b-form-select v-model="selMunicipio" @change="onFilterChange" class="filter-select">
                        <b-form-select-option value="">{{ labels.selectMunicipality }}</b-form-select-option>
                        <option v-for="m in listMunicipio" :key="m.municipio" :value="m.municipio">
                            {{ m.nombre }}
                        </option>
                    </b-form-select>

                    <b-button
                        class="consult-btn ml-2"
                        @click="consultar()"
                        :disabled="!selVigencia || !selFormulario || !selMunicipio || isLoading"
                    >
                        <font-awesome-icon :icon="['fas', 'search']" class="mr-1" />
                        {{ labels.consultar }}
                    </b-button>
                </div>
            </div>
        </div>

        <!-- TARJETA -->
        <div
            class="admin-table-block mt-4"
            :class="{ 'resumen-card': !isLoading && fgl_consulta && filas.length }"
        >
            <div v-if="!fgl_consulta && !isLoading">
                <CorporateDataTable
                    :items="[]" :fields="[]" :labels="tableLabels" :loading="false"
                    :default-per-page="10" :page-options="[10]" :narrow="false"
                    :table-min-height="400" :table-max-height="500"
                    :show-search="false" :allow-column-toggle="false" :enable-sorting="false"
                    :show-pagination="false" :show-rows-per-page="false"
                >
                    <template #empty-state>
                        <div class="text-center py-5">
                            <font-awesome-icon :icon="['fas', 'search']" size="3x" class="text-muted mb-3" style="opacity: 0.3;" />
                            <p class="h4 text-muted">{{ labels.selectFilters }}</p>
                        </div>
                    </template>
                </CorporateDataTable>
            </div>

            <div v-if="isLoading" class="text-center py-5">
                <b-spinner></b-spinner>
                <p class="mt-2">{{ labels.loading }}</p>
            </div>

            <div v-if="!isLoading && fgl_consulta">
                <div v-if="!filas.length" class="text-center py-5">
                    <font-awesome-icon :icon="['fas', 'table']" size="3x" class="text-muted mb-3" style="opacity: 0.3;" />
                    <p class="h4 text-muted">{{ labels.noData }}</p>
                </div>

                <div v-else>
                    <!-- Toolbar -->
                    <div class="resumen-toolbar">
                        <div class="resumen-toolbar-title"></div>
                        <div class="resumen-toolbar-actions">
                            <b-button
                                variant="info" size="sm" class="table-toolbar-export-btn mr-2"
                                @click="mostrarEnMiles = !mostrarEnMiles"
                            >
                                <font-awesome-icon :icon="['fas', mostrarEnMiles ? 'money-bill-wave' : 'calculator']" class="mr-1" />
                                {{ mostrarEnMiles ? 'Ver Pesos Exactos' : 'Ver en Miles' }}
                            </b-button>

                            <b-button
                                variant="success" size="sm" class="table-toolbar-export-btn"
                                @click="exportarExcel" :disabled="!filas.length"
                            >
                                <font-awesome-icon :icon="['fas', 'file-excel']" class="mr-1" />
                                {{ labels.export }}
                            </b-button>
                        </div>
                    </div>

                    <!-- Tabla agrupada -->
                    <div class="bloques-scroll">
                        <div v-for="grupo in gruposConFilas" :key="grupo.grupo" class="resumen-bloque">
                            <h5 class="bloque-titulo">
                                Grupo {{ grupo.grupo }} — {{ grupo.descripcion }}
                            </h5>

                            <CorporateDataTable
                                :items="grupo.filas"
                                :fields="fields"
                                :labels="tableLabels"
                                :narrow="false"
                                :table-min-height="0"
                                :table-max-height="'none'"
                                :show-search="false"
                                :allow-column-toggle="false"
                                :enable-sorting="false"
                                :show-pagination="false"
                                :show-rows-per-page="false"
                            >
                                <template #cell(fila)="data">
                                    <div class="text-center font-weight-bold" :class="{ 'fila-total': data.item?._isTotal }">{{ data.item?.fila }}</div>
                                </template>

                                <template #cell(ciiu)="data">
                                    <div class="text-center" :class="{ 'fila-total': data.item?._isTotal }">
                                        {{ data.item?._ciiu || '—' }}
                                    </div>
                                </template>

                                <template #cell(descripcion)="data">
                                    <div class="text-left" :class="{ 'fila-total': data.item?._isTotal }">
                                        {{ data.item?.descripcion }}
                                    </div>
                                </template>

                                <template #cell(valorBase)="data">
                                    <div class="text-right w-100" :class="{ 'fila-total': data.item?._isTotal }">
                                        {{ data.item?._saldoBase !== undefined ? formatMoney(data.item._saldoBase) : formatMoney(data.item?.valor) }}
                                    </div>
                                </template>

                                <template #cell(tarifa)="data">
                                    <div class="text-right" :class="{ 'fila-total': data.item?._isTotal }">
                                        {{ data.item?._porcentajeFactor !== undefined ? formatPct(data.item._porcentajeFactor) : formatPct(porcentajeGlobal) }}
                                    </div>
                                </template>

                                <template #cell(input)="data">
                                    <div class="d-flex justify-content-center align-items-center flex-wrap gap-1">
                                        <template v-if="data.item?._editable === true">
                                            <b-form-input
                                                v-for="(_, idx) in inputArray(data.item)"
                                                :key="idx"
                                                :value="inputArray(data.item)[idx]"
                                                @input="(v) => onInputChange(data.item, idx, v)"
                                                type="number"
                                                step="0.0001"
                                                size="sm"
                                                class="input-inline"
                                                :placeholder="placeholderFor(data.item, idx)"
                                            ></b-form-input>
                                        </template>
                                    </div>
                                </template>

                                <template #cell(valor)="data">
                                    <div class="text-right font-weight-bold w-100" :class="{ 'fila-total': data.item?._isTotal }">
                                        {{ formatMoney(data.item?.valor) }}
                                    </div>
                                </template>
                            </CorporateDataTable>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import * as XLSX from 'exceljs'
import LiquidacionInfoService from '@/services/LiquidacionInfo'
import CorporateDataTable from '../utils/CorporateDataTable.vue'

/* ============================================================
 * MAPEO FORMULARIO -> REPORTE (sin tocar BD)
 * ============================================================ */
// El reporte vive en tbl_reporte_ingresos_cuentas.reporte.
// Por ahora solo existe el reporte 10 (VALIDADOR INGRESO).
// Cualquier formulario no mapeado usa DEFAULT_REPORTE.
const DEFAULT_REPORTE = 10
const REPORTE_POR_FORMULARIO = {
    // 20: 10,  // ejemplo: si el formulario 20 debe usar el reporte 10
    // 30: 10,
}

function getReporteDeFormulario(formulario) {
    if (!formulario) return DEFAULT_REPORTE
    return REPORTE_POR_FORMULARIO[formulario] !== undefined
        ? REPORTE_POR_FORMULARIO[formulario]
        : DEFAULT_REPORTE
}

/* ============================================================
 * HELPERS
 * ============================================================ */
function toNum(v) {
    const n = Number(v)
    return isNaN(n) ? 0 : n
}

function evalExpresionAritmetica(expr, resolverFila) {
    const tokens = expr.match(/([+-]?\d+)/g) || []
    let resultado = 0
    for (const t of tokens) {
        const num = parseInt(t.replace(/[+-]/, ''), 10)
        const signo = t.startsWith('-') ? -1 : 1
        resultado += signo * toNum(resolverFila(num))
    }
    return resultado
}

function resolverSumaFilas(contenido, resolverFila) {
    if (contenido.includes(',')) {
        return contenido
            .split(',')
            .map((n) => toNum(resolverFila(parseInt(n.trim(), 10))))
            .reduce((a, b) => a + b, 0)
    }
    return evalExpresionAritmetica(contenido, resolverFila)
}

function evaluarFormulacionMulti(formulacion, ctx) {
    if (!formulacion) return 0
    let f = String(formulacion).trim()

    const porcentajes = ctx.porcentajes || []
    let idx = 0
    f = f.replace(/\*porcentaje/g, () => {
        const val = porcentajes[idx] !== undefined ? porcentajes[idx] : ctx.porcentaje
        idx++
        return `*${val}`
    })

    return evaluarExpresionConMultiplicacion(f, ctx)
}

function evaluarExpresionConMultiplicacion(f, ctx) {
    const terminos = f.split('+')
    let total = 0
    for (const t of terminos) {
        total += evaluarTerminoSimple(t.trim(), ctx)
    }
    return total
}

function evaluarTerminoSimple(t, ctx) {
    if (!t) return 0

    let signo = 1
    let expr = t
    if (expr.startsWith('-')) { signo = -1; expr = expr.substring(1) }

    const mMult = expr.match(/^(.*?)\*(\d+(?:\.\d+)?)$/)
    if (mMult) {
        const base = evaluarFormulacionBase(mMult[1], ctx)
        const mult = parseFloat(mMult[2])
        return signo * base * mult
    }

    return signo * evaluarFormulacionBase(expr, ctx)
}

function evaluarFormulacionBase(f, ctx) {
    if (!f) return 0
    const form = String(f).trim()

    if (form.toLowerCase() === 'manual') return toNum(ctx.valorManual)
    if (form === 'valor*tarifa') return toNum(ctx.valorTarifa)

    let m = form.match(/^sumacodigos\((\d+)\)$/)
    if (m) return ctx.sumarPorReporte(parseInt(m[1], 10))

    m = form.match(/^restemunicipio\((\d+)\)$/)
    if (m) return ctx.restarMunicipio(parseInt(m[1], 10))

    m = form.match(/^sumamunicipio\(([\d+]+)\)$/)
    if (m) {
        const codigos = m[1].split('+').map((n) => parseInt(n.trim(), 10))
        return codigos.reduce((acc, c) => acc + ctx.sumarMunicipio(c), 0)
    }

    m = form.match(/^sumafila\((\d+)\)$/)
    if (m) return toNum(ctx.resolverFila(parseInt(m[1], 10)))

    m = form.match(/^sumafilas\(([\d,\-+]+)\)$/)
    if (m) return resolverSumaFilas(m[1], ctx.resolverFila)

    try {
        return evalExpresionAritmetica(form, ctx.resolverFila)
    } catch (e) {
        console.warn('Formulación no reconocida:', form)
        return 0
    }
}

export default {
    name: 'LiquidacionInfo',
    components: { CorporateDataTable },
    data: () => ({
        fgl_consulta: false,
        isLoading: false,
        listVigencia: [],
        listFormulario: [],
        listMunicipio: [],
        selVigencia: '',
        selFormulario: '',
        selMunicipio: '',
        filas: [],
        grupos: [],
        contexto: null,
        inputsUsuario: {},
        nit: '',
        porcentajeGlobal: 0,
        reporteActual: DEFAULT_REPORTE,
        mostrarEnMiles: true
    }),
    computed: {
        labels() {
            if (this.$store.state._language == 'es-ES') {
                return {
                    title: 'Liquidación',
                    selectValidity: 'Seleccione Vigencia',
                    selectForm: 'Seleccione Formulario',
                    selectMunicipality: 'Seleccione Municipio',
                    consultar: 'Consultar',
                    loading: 'Cargando...',
                    noData: 'No hay datos disponibles',
                    selectFilters: 'Seleccione los filtros y consulte',
                    noMatches: 'No se encontraron resultados',
                    searchPlaceholder: 'Buscar en la tabla...',
                    rowsPerPage: 'Filas por página:',
                    noSort: 'Sin orden',
                    sortAsc: 'A-Z / Menor a mayor',
                    sortDesc: 'Z-A / Mayor a menor',
                    columns: 'Columnas',
                    showAllColumns: 'Mostrar todas',
                    filters: 'Filtros',
                    clearFilters: 'Limpiar filtros',
                    export: 'Exportar Excel',
                    etiqueta: 'Descripción',
                    totalGeneral: 'Total general',
                    input: 'Valor a ingresar'
                }
            }
            return {
                title: 'Liquidation',
                selectValidity: 'Select Validity',
                selectForm: 'Select Form',
                selectMunicipality: 'Select Municipality',
                consultar: 'Consult',
                loading: 'Loading...',
                noData: 'No data available',
                selectFilters: 'Select filters and consult',
                noMatches: 'No results found',
                searchPlaceholder: 'Search in table...',
                rowsPerPage: 'Rows per page:',
                noSort: 'No sorting',
                sortAsc: 'A-Z / Low to high',
                sortDesc: 'Z-A / High to low',
                columns: 'Columns',
                showAllColumns: 'Show all',
                filters: 'Filters',
                clearFilters: 'Clear filters',
                export: 'Export Excel',
                etiqueta: 'Description',
                totalGeneral: 'Grand total',
                input: 'Input value'
            }
        },
        tableLabels() {
            return {
                noRow: this.labels.noData,
                noMatches: this.labels.noMatches,
                searchPlaceholder: this.labels.searchPlaceholder,
                rowsPerPage: this.labels.rowsPerPage,
                noSort: this.labels.noSort,
                sortAsc: this.labels.sortAsc,
                sortDesc: this.labels.sortDesc,
                columns: this.labels.columns,
                showAllColumns: this.labels.showAllColumns,
                filters: this.labels.filters,
                clearFilters: this.labels.clearFilters
            }
        },
        fields() {
            return [
                { key: 'fila', label: 'Fila', thClass: 'text-center', tdClass: 'text-center align-middle', style: 'width: 100px;' },
                { key: 'ciiu', label: 'CIIU', thClass: 'text-center', tdClass: 'text-center align-middle', style: 'width: 80px;' },
                { key: 'descripcion', label: this.labels.etiqueta, thClass: 'text-left', tdClass: 'text-left align-middle' },
                { key: 'valorBase', label: 'Base', thClass: 'text-right', tdClass: 'text-right align-middle', style: 'width: 150px;' },
                { key: 'tarifa', label: 'Tarifa', thClass: 'text-right', tdClass: 'text-right align-middle', style: 'width: 100px;' },
                { key: 'input', label: this.labels.input, thClass: 'text-center', tdClass: 'text-center align-middle', thStyle: { width: '260px' } },
                { key: 'valor', label: 'Valor a Pagar', thClass: 'text-right', tdClass: 'text-right align-middle', style: 'width: 150px;' }
            ]
        },
        gruposConFilas() {
            if (!this.filas.length) return []
            const mapGrupos = {}
            this.grupos.forEach((g) => {
                mapGrupos[g.grupo] = { grupo: g.grupo, descripcion: g.descripcion, filas: [] }
            })
            this.filas.forEach((f) => {
                if (!mapGrupos[f.grupo]) {
                    mapGrupos[f.grupo] = { grupo: f.grupo, descripcion: `Grupo ${f.grupo}`, filas: [] }
                }
                mapGrupos[f.grupo].filas.push(f)
            })
            return Object.values(mapGrupos).filter((g) => g.filas.length > 0)
        }
    },
    methods: {
        goBack() { this.$router.back() },

        // ============================================================
        // REPORTE RESUELTO PARA UN FORMULARIO
        // ============================================================
        getReporteDeFormulario(formulario) {
            return getReporteDeFormulario(formulario)
        },

        onFormularioChange() {
            // Al cambiar de formulario, resolvemos su reporte
            this.reporteActual = this.getReporteDeFormulario(this.selFormulario)
            this.onFilterChange()
        },

        onFilterChange() {
            this.filas = []
            this.grupos = []
            this.contexto = null
            this.inputsUsuario = {}
            this.fgl_consulta = false
        },

        formatMoney(value) {
            let num = Number(value) || 0

            // Si está activado mostrar en miles, redondeamos al múltiplo de 1000 más cercano
            if (this.mostrarEnMiles) {
                num = Math.round(num / 1000) * 1000
            }

            return num.toLocaleString('es-CO', {
                minimumFractionDigits: 0,
                maximumFractionDigits: 0
            })
        },

        formatPct(value) {
            const num = Number(value) || 0
            return (num * 100).toLocaleString('es-CO', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 4
            }) + ' %'
        },

        inputArray(item) {
            if (!item?._editable) return []
            const cantidad = Number(item._cantidadInputs) || 1
            const guardado = this.inputsUsuario[item.id]
            if (Array.isArray(guardado)) {
                const arr = guardado.slice(0, cantidad)
                while (arr.length < cantidad) {
                    arr.push(item._valoresInput?.[arr.length] ?? 0)
                }
                return arr
            }
            const iniciales = Array.isArray(item._valoresInput) ? item._valoresInput : [0]
            const arr = iniciales.slice(0, cantidad)
            while (arr.length < cantidad) arr.push(0)
            return arr
        },

        placeholderFor(item, idx) {
            if (item?._tipoInput === 'porcentaje') {
                if (item._etiquetasInput && item._etiquetasInput[idx]) {
                    return item._etiquetasInput[idx];
                }
                return '%';
            }
            return 'Valor';
        },

        onInputChange(item, idx, val) {
            if (!item?._editable) return
            const num = val === '' || val === null ? 0 : Number(val)
            const arr = this.inputArray(item).slice()
            arr[idx] = num
            this.inputsUsuario[item.id] = arr
            this.recalcularTodo()
        },

        recalcularTodo() {
            if (!this.contexto) return
            const ctxData = this.contexto

            const subtotalesCiiu = {}
            this.filas.forEach((f) => {
                if (f._isDetalle && f._ciiu) {
                    subtotalesCiiu[f._ciiu] = toNum(f.valor)
                }
            })

            const resultadosPorFila = {}
            const filasPrincipales = this.filas.filter((f) => !f._isDetalle)

            filasPrincipales.forEach((f) => {
                const esPorCiiu = Number(f._filaporciiu) === 1
                const esValorTarifa = String(f._formulacionRaw).trim() === 'valor*tarifa'

                let valorManual = 0
                if (f._editable && f._tipoInput === 'manual') {
                    const arr = this.inputArray(f)
                    valorManual = toNum(arr[0])
                }

                let valorTarifa = 0
                if (esValorTarifa) {
                    if (esPorCiiu) {
                        valorTarifa = Object.values(subtotalesCiiu).reduce((a, b) => a + b, 0)
                    } else {
                        const valorGrupo = ctxData.saldosPorGrupo[f.grupo] || 0
                        valorTarifa = valorGrupo * toNum(ctxData.porcentaje)
                    }
                }

                let porcentajes = []
                if (f._editable && f._tipoInput === 'porcentaje') {
                    const arr = this.inputArray(f)
                    porcentajes = arr.map((v) => toNum(v) / 100)
                } else {
                    porcentajes = [ctxData.porcentaje]
                }

                const ctx = {
                    porcentaje: ctxData.porcentaje,
                    porcentajes,
                    valorManual,
                    valorTarifa,
                    resolverFila: (fila) => resultadosPorFila[fila] ?? 0,
                    sumarPorReporte: (codigo) => ctxData.saldosGlobalesPorCodigo[codigo] || 0,
                    restarMunicipio: (codigo) => {
                        const global = ctxData.saldosGlobalesPorCodigo[codigo] || 0
                        const mun = ctxData.saldosMunicipioPorCodigo[codigo] || 0
                        return global - mun
                    },
                    sumarMunicipio: (codigo) => ctxData.saldosMunicipioPorCodigo[codigo] || 0
                }

                let valor = 0
                if (esValorTarifa) {
                    valor = valorTarifa
                } else if (f._editable && f._tipoInput === 'manual') {
                    valor = valorManual
                } else {
                    valor = evaluarFormulacionMulti(f._formulacionRaw, ctx)
                }

                resultadosPorFila[f.fila] = valor
                f.valor = valor
            })
        },

        consultar() {
            if (!this.selVigencia || !this.selFormulario || !this.selMunicipio) {
                this.$toast.error('Seleccione vigencia, formulario y municipio.')
                return
            }
            this.isLoading = true
            this.inputsUsuario = {}

            // Resolvemos el reporte a usar. Si el backend lo soporta, lo usará;
            // si no, ignora el campo extra y sigue usando `formulario` como antes.
            const reporte = this.getReporteDeFormulario(this.selFormulario)
            this.reporteActual = reporte

            LiquidacionInfoService.getLiquidacion({
                vigencia: this.selVigencia,
                formulario: this.selFormulario,
                reporte: reporte, // nuevo campo (retrocompatible)
                nit: this.nit,
                municipio: this.selMunicipio
            })
                .then((res) => {
                    const data = res.data.data || {}
                    this.filas = data.filas || []
                    this.grupos = data.grupos || []
                    this.contexto = data.contexto || null
                    this.porcentajeGlobal = data.porcentaje || 0
                    this.fgl_consulta = true
                    this.$nextTick(() => this.recalcularTodo())
                })
                .catch((err) => {
                    console.error(err)
                    this.filas = []
                    this.grupos = []
                    this.contexto = null
                    this.fgl_consulta = true
                    this.$toast.error('Error al consultar la liquidación')
                })
                .finally(() => { this.isLoading = false })
        },

        cargarCombos() {
            this.isLoading = true
            Promise.all([
                LiquidacionInfoService.getVigencias(),
                LiquidacionInfoService.getFormularios(),
                LiquidacionInfoService.getMunicipios(this.nit)
            ])
                .then(([vigRes, formRes, munRes]) => {
                    this.listVigencia = vigRes.data.vigencias || []
                    this.listFormulario = formRes.data.formularios || []
                    this.listMunicipio = munRes.data.municipios || []
                })
                .catch((err) => {
                    console.error(err)
                    this.$toast.error('Error al cargar filtros')
                })
                .finally(() => { this.isLoading = false })
        },

        async exportarExcel() {
            if (this._exportandoFlag) return
            if (!this.filas.length) {
                this.$toast.warning('No hay datos para exportar')
                return
            }
            this._exportandoFlag = true
            try {
                const gruposCopia = JSON.parse(JSON.stringify(this.gruposConFilas))
                const { Workbook } = XLSX
                const workbook = new Workbook()
                const worksheet = workbook.addWorksheet('Liquidación')

                const columnCount = 6

                const styles = {
                    titulo: {
                        font: { name: 'Calibri', size: 16, bold: true, color: { argb: '000000' } },
                        alignment: { horizontal: 'center', vertical: 'middle' },
                        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFF' } },
                        border: { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } }
                    },
                    subtitulo: {
                        font: { name: 'Calibri', size: 11, bold: true, color: { argb: '000000' } },
                        alignment: { horizontal: 'center', vertical: 'middle' },
                        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFF' } },
                        border: { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } }
                    },
                    bloqueTitulo: {
                        font: { name: 'Calibri', size: 12, bold: true, color: { argb: '000000' } },
                        alignment: { horizontal: 'left', vertical: 'middle' },
                        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'D9EAF3' } },
                        border: { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } }
                    },
                    encabezado: {
                        font: { name: 'Calibri', size: 11, bold: true, color: { argb: 'FFFFFF' } },
                        alignment: { horizontal: 'center', vertical: 'middle' },
                        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: '1F4F83' } },
                        border: { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } }
                    },
                    datoTexto: {
                        font: { name: 'Calibri', size: 10, color: { argb: '000000' } },
                        alignment: { horizontal: 'left', vertical: 'middle' },
                        border: { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } }
                    },
                    datoCentro: {
                        font: { name: 'Calibri', size: 10, color: { argb: '000000' } },
                        alignment: { horizontal: 'center', vertical: 'middle' },
                        border: { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } }
                    },
                    datoNumero: {
                        font: { name: 'Calibri', size: 10, color: { argb: '000000' } },
                        alignment: { horizontal: 'right', vertical: 'middle' },
                        numFmt: '#,##0',
                        border: { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } }
                    },
                    filaTotalTexto: {
                        font: { name: 'Calibri', size: 10, bold: true, color: { argb: '000000' } },
                        alignment: { horizontal: 'left', vertical: 'middle' },
                        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'EEF4F8' } },
                        border: { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } }
                    },
                    filaTotalNumero: {
                        font: { name: 'Calibri', size: 10, bold: true, color: { argb: '000000' } },
                        alignment: { horizontal: 'right', vertical: 'middle' },
                        numFmt: '#,##0',
                        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'EEF4F8' } },
                        border: { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } }
                    }
                }

                const usuario = this.$store?.state?.userName || 'Usuario'
                const fechaGeneracion = new Date().toLocaleString('es-CO')
                const nombreFormulario = (this.listFormulario || []).find(f => String(f.formulario) === String(this.selFormulario))?.descripcion || `Formulario ${this.selFormulario}`
                const nombreMunicipio = (this.listMunicipio || []).find(m => m.municipio === this.selMunicipio)?.nombre || this.selMunicipio

                let currentRow = 1

                worksheet.mergeCells(currentRow, 1, currentRow, columnCount)
                worksheet.getCell(currentRow, 1).value = 'LIQUIDACIÓN DE IMPUESTOS'
                Object.assign(worksheet.getCell(currentRow, 1), styles.titulo)
                currentRow++

                worksheet.mergeCells(currentRow, 1, currentRow, columnCount)
                worksheet.getCell(currentRow, 1).value = `${nombreFormulario} | Vigencia: ${this.selVigencia} | Municipio: ${nombreMunicipio}`
                Object.assign(worksheet.getCell(currentRow, 1), styles.subtitulo)
                currentRow++

                worksheet.mergeCells(currentRow, 1, currentRow, columnCount)
                worksheet.getCell(currentRow, 1).value = `Generado: ${fechaGeneracion} por ${usuario}`
                Object.assign(worksheet.getCell(currentRow, 1), styles.subtitulo)
                currentRow++

                currentRow++

                worksheet.columns = [
                    { width: 12 },
                    { width: 50 },
                    { width: 20 },
                    { width: 15 },
                    { width: 22 },
                    { width: 20 }
                ]

                gruposCopia.forEach((grupo) => {
                    worksheet.mergeCells(currentRow, 1, currentRow, columnCount)
                    worksheet.getCell(currentRow, 1).value = `Grupo ${grupo.grupo} — ${grupo.descripcion}`
                    Object.assign(worksheet.getCell(currentRow, 1), styles.bloqueTitulo)
                    currentRow++

                    const headerValues = ['Fila', 'Descripción', 'Base', 'Tarifa', 'Valor ingresado', 'Valor a Pagar']
                    headerValues.forEach((val, idx) => {
                        const cell = worksheet.getCell(currentRow, idx + 1)
                        cell.value = val
                        Object.assign(cell, styles.encabezado)
                    })
                    currentRow++

                    grupo.filas.forEach((f) => {
                        const esTotal = f._isTotal === true
                        
                        let valorIngresado = ''
                        if (f._editable) {
                            const arr = this.inputArray(f)
                            const hayValores = arr.some(v => v !== 0 && v !== null && v !== '')
                            if (hayValores) {
                                valorIngresado = arr.join(' | ')
                            } else if (f._etiquetasInput && f._etiquetasInput.length > 0) {
                                valorIngresado = f._etiquetasInput.join(' | ')
                            }
                        }

                        let valorBase = f._saldoBase !== undefined ? f._saldoBase : f.valor
                        let tarifa = f._porcentajeFactor !== undefined ? f._porcentajeFactor : this.porcentajeGlobal

                        const c1 = worksheet.getCell(currentRow, 1)
                        c1.value = f.fila
                        Object.assign(c1, esTotal ? styles.filaTotalTexto : styles.datoCentro)

                        const c2 = worksheet.getCell(currentRow, 2)
                        c2.value = f.descripcion
                        Object.assign(c2, esTotal ? styles.filaTotalTexto : styles.datoTexto)
                        if (f._isDetalle) c2.alignment = { indent: 2, vertical: 'middle' }

                        const c3 = worksheet.getCell(currentRow, 3)
                        let baseParaExcel = Number(valorBase) || 0;
                        if (this.mostrarEnMiles) baseParaExcel = Math.round(baseParaExcel / 1000) * 1000;
                        
                        c3.value = Math.round(baseParaExcel)
                        Object.assign(c3, esTotal ? styles.filaTotalNumero : styles.datoNumero)

                        const c4 = worksheet.getCell(currentRow, 4)
                        c4.value = Math.round(Number(tarifa) * 100) / 100 
                        Object.assign(c4, esTotal ? styles.filaTotalNumero : styles.datoNumero)
                        c4.numFmt = '0.00%'

                        const c5 = worksheet.getCell(currentRow, 5)
                        c5.value = valorIngresado
                        Object.assign(c5, esTotal ? styles.filaTotalTexto : styles.datoCentro)

                        const c6 = worksheet.getCell(currentRow, 6)
                        let valorParaExcel = Number(f.valor) || 0;
                        if (this.mostrarEnMiles) valorParaExcel = Math.round(valorParaExcel / 1000) * 1000;

                        c6.value = Math.round(valorParaExcel)
                        Object.assign(c6, esTotal ? styles.filaTotalNumero : styles.datoNumero)

                        currentRow++
                    })
                    currentRow++
                })

                worksheet.views = [{ state: 'frozen', ySplit: 4 }]

                const buffer = await workbook.xlsx.writeBuffer()
                const blob = new Blob([buffer], {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                })
                const a = document.createElement('a')
                const fechaStr = new Date().toISOString().slice(0, 10)
                a.href = URL.createObjectURL(blob)
                a.download = `Liquidacion_${this.selFormulario}_${this.selVigencia}_${fechaStr}.xlsx`
                document.body.appendChild(a)
                a.click()
                document.body.removeChild(a)
                URL.revokeObjectURL(a.href)

                this.$toast.success('Excel exportado exitosamente')
            } catch (error) {
                console.error('Error al exportar:', error)
                this.$toast.error('Error al exportar: ' + error.message)
            } finally {
                this._exportandoFlag = false
            }
        }
    },
    mounted() {
        this._exportandoFlag = false
        this.nit = this.$store.state.companyId || '22222222'
        this.cargarCombos()
    }
}
</script>

<style src="../main.css"></style>

<style scoped>
.niif-header .filters-container { display: flex; align-items: center; gap: 8px; flex-shrink: 0; margin-left: 0 !important; }
.niif-header .filter-select { min-width: 200px; max-width: 280px; width: auto; height: 38px; font-size: 14px; border-radius: 7px; }
.niif-header .filter-select option { white-space: normal; word-break: break-word; padding: 4px 8px; }
.niif-header .consult-btn { height: 38px; min-height: 38px; padding: 0 20px; display: inline-flex; align-items: center; justify-content: center; white-space: nowrap; border-radius: 7px; font-size: 14px; background-color: #6c757d !important; color: white !important; border: 1px solid #6c757d !important; cursor: pointer; flex-shrink: 0; }
.niif-header .consult-btn:hover, .niif-header .consult-btn:focus, .niif-header .consult-btn:active, .niif-header .consult-btn:not(:disabled):hover, .niif-header .consult-btn:not(:disabled):focus, .niif-header .consult-btn:not(:disabled):active { background-color: #6c757d !important; color: white !important; border-color: #6c757d !important; box-shadow: none !important; }
.niif-header .consult-btn:disabled { opacity: 0.65; cursor: not-allowed; background-color: #6c757d !important; color: white !important; border-color: #6c757d !important; }
.page-back-btn { padding: 0; font-size: 1.5rem; line-height: 1; }
.back-icon { font-size: 1.75rem !important; width: 1.75rem !important; height: 1.75rem !important; }
.param-title { font-size: 2rem !important; font-weight: 500; }

.resumen-card { background: #fff !important; border-radius: 30px !important; box-shadow: 0 3px 12px rgba(0, 0, 0, 0.12) !important; padding: 40px !important; }
.resumen-toolbar { display: flex; align-items: center; justify-content: space-between; margin-bottom: 16px; gap: 12px; }
.resumen-toolbar-actions { display: flex; align-items: center; gap: 8px; margin-left: auto; }
.table-toolbar-export-btn { height: 31px; min-height: 31px; padding: 0 14px; display: inline-flex; align-items: center; justify-content: center; white-space: nowrap; border-radius: 7px; font-size: 13px; background-color: #1f4f83 !important; color: white !important; border: 1px solid #1f4f83 !important; }
.table-toolbar-export-btn:hover:not(:disabled) { background-color: #1a446f !important; border-color: #1a446f !important; }
.table-toolbar-export-btn:disabled { opacity: 0.65; cursor: not-allowed; }

.bloques-scroll { max-height: 70vh; overflow-y: auto; padding-right: 8px; }
.bloques-scroll::-webkit-scrollbar { width: 8px; }
.bloques-scroll::-webkit-scrollbar-thumb { background: #c7d0d9; border-radius: 4px; }
.bloques-scroll::-webkit-scrollbar-track { background: #f1f5f9; border-radius: 4px; }

.resumen-bloque { padding-bottom: 0; margin-bottom: 0; border-bottom: none; }
.resumen-bloque + .resumen-bloque { margin-top: 8px; }
.bloque-titulo { background: #d9eaf3; padding: 8px 12px; border-radius: 4px; font-weight: 600; margin: 0 0 4px 0; }
.fila-total { background: #eef4f8; }

.input-inline { max-width: 110px; height: 30px; font-size: 13px; text-align: right; padding: 2px 8px; }
.gap-1 { gap: 4px; }

.resumen-bloque ::v-deep .table-corporate-shell,
.resumen-bloque ::v-deep .table-corporate-body,
.resumen-bloque ::v-deep .tblEstandar,
.resumen-bloque ::v-deep .tblCorporate,
.resumen-bloque ::v-deep .b-table-sticky-header,
.resumen-bloque ::v-deep .table-responsive,
.resumen-bloque ::v-deep .card,
.resumen-bloque ::v-deep .admin-table-block,
.resumen-bloque ::v-deep .corporate-data-table {
    background: transparent !important;
    box-shadow: none !important;
    border: none !important;
    border-radius: 0 !important;
    padding: 0 !important;
    margin: 0 !important;
    max-width: 100% !important;
}
.resumen-bloque ::v-deep .table-corporate-shell,
.resumen-bloque ::v-deep .table-corporate-body,
.resumen-bloque ::v-deep .table-responsive,
.resumen-bloque ::v-deep .tblEstandar,
.resumen-bloque ::v-deep .tblCorporate {
    margin: 0 !important; padding: 0 !important;
}
.resumen-bloque ::v-deep table.table { margin-bottom: 0 !important; }
.resumen-bloque ::v-deep .table-corporate-toolbar { display: none !important; }
.resumen-bloque ::v-deep .table-corporate-body { min-height: 0 !important; height: auto !important; }
.resumen-bloque ::v-deep .table-corporate-shell,
.resumen-bloque ::v-deep .table-corporate-body,
.resumen-bloque ::v-deep .table-responsive,
.resumen-bloque ::v-deep .b-table-sticky-header,
.resumen-bloque ::v-deep .tblEstandar,
.resumen-bloque ::v-deep .tblCorporate,
.resumen-bloque ::v-deep table {
    min-height: 0 !important; height: auto !important; max-height: none !important;
}
.resumen-bloque ::v-deep tbody tr:hover,
.resumen-bloque ::v-deep tbody tr:hover > td,
.resumen-bloque ::v-deep tbody tr:hover > th,
.resumen-bloque ::v-deep tbody tr:hover > * {
    background: transparent !important; background-color: transparent !important; background-image: none !important; box-shadow: none !important; color: inherit !important; --bs-table-accent-bg: transparent !important; --bs-table-hover-bg: transparent !important;
}
.resumen-bloque ::v-deep tbody tr { cursor: default !important; }

@media (max-width: 1200px) {
    .niif-header .filter-select { min-width: 160px; max-width: 220px; height: 36px; font-size: 13px; }
    .niif-header .consult-btn { height: 36px; min-height: 36px; font-size: 13px; padding: 0 16px; }
}
@media (max-width: 992px) {
    .niif-header .filters-container { flex-wrap: wrap; width: 100%; margin-top: 8px; margin-left: 0 !important; justify-content: flex-start; gap: 6px; }
    .niif-header .filter-select { min-width: 150px; max-width: 200px; height: 36px; font-size: 13px; flex: 1 1 auto; }
    .niif-header .consult-btn { height: 36px; min-height: 36px; font-size: 13px; padding: 0 16px; }
}
@media (max-width: 576px) {
    .param-title { font-size: 1.5rem !important; }
    .niif-header .filter-select { min-width: 100%; max-width: 100%; height: 36px; font-size: 13px; flex: 0 0 100%; }
    .niif-header .filters-container { gap: 4px; }
    .niif-header .consult-btn { width: 100%; flex: 0 0 100%; height: 36px; margin-left: 0 !important; margin-top: 4px; }
}
.input-with-label {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
}
.input-label {
    font-size: 10px;
    color: #6c757d;
    line-height: 1;
    white-space: nowrap;
    font-weight: 600;
}

.resumen-bloque ::v-deep td.text-right,
.resumen-bloque ::v-deep td.text-right > div,
.resumen-bloque ::v-deep .tblCorporate td:nth-child(4),
.resumen-bloque ::v-deep .tblCorporate td:nth-child(5),
.resumen-bloque ::v-deep .tblCorporate td:nth-child(7) {
    text-align: right !important;
}

.resumen-bloque ::v-deep td.text-right > div {
    width: 100%;
    display: block;
}
</style>